import {
  ArrowTopRightOnSquareIcon,
  ClipboardDocumentIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { isValidElement, type ReactNode, useEffect, useRef, useState } from 'react';
import Markdown from 'react-markdown';
import { formatAppName } from '../lib/appName';
import { copyText } from '../lib/clipboard';
import { formatRelativeTimestamp, formatTimestamp } from '../lib/history';
import { recognizeUrl } from '../lib/urlRecognition';
import type { VerificationResult } from '../types';
import { CopyableVersionTag } from './CopyableVersionTag';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';

interface ValidationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deploymentId: number;
  appName: string;
  environment: string;
  version: string;
  deployedAt: string;
  verification?: VerificationResult;
  validationInfo?: string;
  triggerUrls?: string[];
  logsUrls?: string[];
  onValidate: (deploymentId: number) => Promise<void>;
}

const getTextContent = (node: ReactNode): string => {
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node);
  }

  if (Array.isArray(node)) {
    return node.map(getTextContent).join('');
  }

  if (isValidElement<{ children?: ReactNode }>(node)) {
    return getTextContent(node.props.children);
  }

  return '';
};

const MarkdownCodeBlock = ({ children }: { children?: ReactNode }) => {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<number>();

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleCopy = async () => {
    try {
      await copyText(getTextContent(children).trimEnd());
      setCopied(true);
    } catch (error) {
      console.error('Failed to copy code block:', error);
      return;
    }

    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = window.setTimeout(() => {
      setCopied(false);
    }, 1800);
  };

  return (
    <div className='relative'>
      <button
        type='button'
        onClick={handleCopy}
        className='absolute right-2 top-2 rounded bg-white/15 p-1 text-primary-foreground transition-colors hover:bg-white/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-foreground cursor-pointer'
        aria-label='Copy code block'
        title='Copy code'
      >
        <ClipboardDocumentIcon className='h-4 w-4' />
      </button>
      {copied && (
        <div
          role='status'
          className='absolute right-0 top-0 z-50 -translate-y-full whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs text-white shadow-md'
        >
          Copied
        </div>
      )}
      <pre className='pr-10'>{children}</pre>
    </div>
  );
};

export const ValidationDialog = ({
  open,
  onOpenChange,
  deploymentId,
  appName,
  environment,
  version,
  deployedAt,
  verification,
  validationInfo,
  triggerUrls,
  logsUrls,
  onValidate,
}: ValidationDialogProps) => {
  const [isValidating, setIsValidating] = useState(false);

  // Determine actual validation state based on verification prop
  // The verification prop is the source of truth, not the validationMissing prop
  const isValidated = !!verification;

  const handleValidate = async () => {
    setIsValidating(true);
    try {
      await onValidate(deploymentId);
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to validate deployment:', error);
      // TODO: Show error message to user
    } finally {
      setIsValidating(false);
    }
  };

  const envColorClass =
    environment === 'prod'
      ? 'bg-green-100 text-green-800'
      : environment === 'dev'
        ? 'bg-blue-100 text-blue-800'
        : 'bg-gray-100 text-gray-700';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='min-w-lg'>
        <DialogHeader>
          <div className='flex items-start justify-between gap-3'>
            <div className='flex-1 min-w-0'>
              <DialogTitle>{formatAppName(appName)}</DialogTitle>
            </div>
            <div className='flex items-center gap-1.5 flex-shrink-0'>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${envColorClass}`}>
                {environment}
              </span>
              <CopyableVersionTag version={version} />
            </div>
          </div>
        </DialogHeader>

        {isValidated && (
          <div className='space-y-3'>
            <div>
              <div className='text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5'>
                Validated by
              </div>
              <div className='text-sm text-gray-900'>{verification.createdBy}</div>
            </div>
            <div>
              <div className='text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5'>
                Validated at
              </div>
              <div className='text-sm text-gray-900'>{formatTimestamp(verification.createdAt)}</div>
            </div>
          </div>
        )}

        {validationInfo && (
          <div className='mt-4 p-3 bg-primary border border-primary rounded flex gap-2'>
            <InformationCircleIcon className='w-5 h-5 text-primary-foreground shrink-0' />
            <div className='flex-1 min-w-0 prose-light text-sm'>
              <Markdown
                components={{
                  a: ({ href, children }) => (
                    <a
                      href={href}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='inline-flex items-center gap-1'
                    >
                      {children}
                      <ArrowTopRightOnSquareIcon className='inline-block h-3.5 w-3.5 shrink-0' />
                    </a>
                  ),
                  pre: ({ children }) => <MarkdownCodeBlock>{children}</MarkdownCodeBlock>,
                }}
              >
                {validationInfo}
              </Markdown>
            </div>
          </div>
        )}

        {(triggerUrls && triggerUrls.length > 0) || (logsUrls && logsUrls.length > 0) ? (
          <div className='mt-4 space-y-2'>
            {triggerUrls && triggerUrls.length > 0 && (
              <div>
                <div className='text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5'>
                  Trigger
                </div>
                <div className='space-y-1'>
                  {triggerUrls.map((url, index) => {
                    const urlInfo = recognizeUrl(url);
                    return (
                      <a
                        key={index}
                        href={url}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='flex items-center gap-1.5 text-sm text-primary hover:underline'
                        title={url}
                      >
                        <ArrowTopRightOnSquareIcon className='w-4 h-4' />
                        {urlInfo.label}
                      </a>
                    );
                  })}
                </div>
              </div>
            )}

            {logsUrls && logsUrls.length > 0 && (
              <div>
                <div className='text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5'>
                  Logs
                </div>
                <div className='space-y-1'>
                  {logsUrls.map((url, index) => {
                    const urlInfo = recognizeUrl(url);
                    return (
                      <a
                        key={index}
                        href={url}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='flex items-center gap-1.5 text-sm text-primary hover:underline'
                        title={url}
                      >
                        <ArrowTopRightOnSquareIcon className='w-4 h-4' />
                        {urlInfo.label}
                      </a>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ) : null}

        {!isValidated && (
          <div className='mt-4'>
            <div className='text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5'>
              Deployed
            </div>
            <div className='text-sm text-gray-900' title={formatTimestamp(deployedAt)}>
              {formatRelativeTimestamp(deployedAt)}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant='outline' onClick={() => onOpenChange(false)} disabled={isValidating}>
            {isValidated ? 'Close' : 'Cancel'}
          </Button>
          {!isValidated && (
            <Button onClick={handleValidate} disabled={isValidating}>
              {isValidating ? 'Validating...' : 'Mark as Validated'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
